import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, UserPlus, Clock, AlertTriangle, RefreshCw, Loader2, CheckSquare, Square, ZapOff, BadgeCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import HealthChip from './HealthChip';
import UserEngagementDrawer from './UserEngagementDrawer';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';
const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / 86400000);
};

const TrialsPage = () => {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [extendDays, setExtendDays] = useState(7);
  const [extending, setExtending] = useState(false);
  const [drawerUserId, setDrawerUserId] = useState(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newDays, setNewDays] = useState(7);
  const [creating, setCreating] = useState(false);

  // Convert confirmation
  const [convertTarget, setConvertTarget] = useState(null);
  const [converting, setConverting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setRefreshing(true);
      const [s, l] = await Promise.all([
        api.get('/admin/trials/summary'),
        api.get('/admin/trials'),
      ]);
      setSummary(s.data);
      setItems(l.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load trials');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map(i => i.id)));
  };

  const extendSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      setExtending(true);
      const r = await api.post('/admin/trials/extend', { days: extendDays, user_ids: Array.from(selectedIds) });
      toast.success(`Extended ${r.data.updated} trial(s) by ${extendDays} days`);
      setSelectedIds(new Set());
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extend failed');
    } finally { setExtending(false); }
  };

  const extendAll = async () => {
    try {
      setExtending(true);
      const r = await api.post('/admin/trials/extend', { days: extendDays, all_active_trials: true });
      toast.success(`Extended all ${r.data.updated} active trials by ${extendDays} days`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extend failed');
    } finally { setExtending(false); }
  };

  const createTrial = async () => {
    try {
      setCreating(true);
      const r = await api.post('/admin/trials/create', {
        email: newEmail, full_name: newName,
        password: newPwd || undefined, trial_days: newDays,
      });
      if (r.data.temporary_password) {
        toast.success(`Trial created — temp password: ${r.data.temporary_password}`, { duration: 12000 });
      } else {
        toast.success(`Trial user ${r.data.email} created`);
      }
      setCreateOpen(false); setNewEmail(''); setNewName(''); setNewPwd(''); setNewDays(14);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  const convertToPaid = async () => {
    if (!convertTarget) return;
    try {
      setConverting(true);
      await api.post(`/admin/trials/convert-to-paid/${convertTarget.id}`);
      toast.success(`${convertTarget.email} converted to active subscription`);
      setConvertTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Convert failed');
    } finally { setConverting(false); }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[300px]"><Loader2 className="h-8 w-8 animate-spin text-red-400" /></div>;
  }

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  return (
    <div className="p-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2" data-testid="trials-title">Trial Management</h1>
          <p className="text-slate-300">Bulk-extend, create demo trials, and manually activate paid subscriptions for offline payments.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateOpen(true)} className="bg-[#DC2626] hover:bg-[#3a7ec0]" data-testid="open-create-trial">
            <UserPlus className="h-4 w-4 mr-1" /> Create trial user
          </Button>
          <Button variant="outline" onClick={fetchAll} disabled={refreshing} data-testid="refresh-trials">
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-5"><div className="text-xs uppercase text-gray-500 mb-1">Active trials</div><div className="text-3xl font-bold text-red-400" data-testid="trials-active">{summary?.active_trials ?? 0}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-xs uppercase text-gray-500 mb-1">Expiring within 7 days</div><div className="text-3xl font-bold text-orange-400" data-testid="trials-expiring">{summary?.expiring_within_7d ?? 0}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-xs uppercase text-gray-500 mb-1">Expired in last 7 days</div><div className="text-3xl font-bold text-amber-400" data-testid="trials-recent-expired">{summary?.expired_last_7d ?? 0}</div></CardContent></Card>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Bulk extend</CardTitle>
          <CardDescription>Add days to selected trials, or to every active trial at once.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="ext-days">Days to add</Label>
            <Input id="ext-days" type="number" min={1} max={365} value={extendDays} onChange={e => setExtendDays(parseInt(e.target.value, 10) || 1)} className="w-32" data-testid="extend-days-input" />
          </div>
          <Button onClick={extendSelected} disabled={extending || selectedIds.size === 0} data-testid="extend-selected-btn">
            <Clock className="h-4 w-4 mr-1" /> Extend {selectedIds.size} selected
          </Button>
          <Button onClick={extendAll} variant="outline" disabled={extending || (summary?.active_trials ?? 0) === 0} data-testid="extend-all-btn">
            <Calendar className="h-4 w-4 mr-1" /> Extend ALL {summary?.active_trials ?? 0} active trials
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Active trial users</CardTitle><CardDescription>Sorted by trial end (soonest first)</CardDescription></CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-10">No active trial users.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 uppercase border-b bg-[#0A1024]">
                  <tr>
                    <th className="text-left py-2 px-3 w-10">
                      <button onClick={toggleSelectAll} aria-label="Select all" data-testid="select-all-trials">
                        {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                      </button>
                    </th>
                    <th className="text-left py-2 px-3">Email</th>
                    <th className="text-left py-2 px-3">Full name</th>
                    <th className="text-left py-2 px-3">Health</th>
                    <th className="text-left py-2 px-3">Signals</th>
                    <th className="text-left py-2 px-3">Trial end</th>
                    <th className="text-center py-2 px-3">Days left</th>
                    <th className="text-right py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(u => {
                    const left = daysUntil(u.trial_end);
                    const lowBadge = left !== null && left <= 3 ? <Badge className="bg-amber-900/30 text-amber-300">{left}d</Badge> : left !== null && left <= 7 ? <Badge className="bg-orange-900/30 text-orange-300">{left}d</Badge> : <Badge variant="outline">{left}d</Badge>;
                    return (
                      <tr key={u.id} className="border-b hover:bg-[#0A1024]" data-testid={`trial-row-${u.id}`}>
                        <td className="py-2 px-3">
                          <button onClick={() => toggleSelected(u.id)} aria-label="Select" data-testid={`select-trial-${u.id}`}>
                            {selectedIds.has(u.id) ? <CheckSquare className="h-4 w-4 text-red-400" /> : <Square className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="py-2 px-3 font-medium">{u.email}</td>
                        <td className="py-2 px-3">{u.full_name || '—'}</td>
                        <td className="py-2 px-3">
                          <button
                            onClick={() => setDrawerUserId(u.id)}
                            className="hover:opacity-80 focus:outline-none"
                            data-testid={`trial-health-${u.id}`}
                            title="View activity"
                          >
                            <HealthChip status={u.health_status || 'never_activated'} />
                          </button>
                        </td>
                        <td className="py-2 px-3 text-xs text-slate-300" data-testid={`trial-signal-count-${u.id}`}>
                          {u.signal_count ?? 0}
                        </td>
                        <td className="py-2 px-3 text-xs text-slate-300">{fmtDate(u.trial_end)}</td>
                        <td className="py-2 px-3 text-center">{lowBadge}</td>
                        <td className="py-2 px-3 text-right">
                          <Button size="sm" variant="outline" className="text-emerald-300 border-emerald-800/50" onClick={() => setConvertTarget(u)} data-testid={`convert-trial-${u.id}`}>
                            <BadgeCheck className="h-3 w-3 mr-1" /> Mark as paid
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CREATE DIALOG */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-trial-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5" /> Create trial user</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Email</Label><Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@example.com" data-testid="new-trial-email" /></div>
            <div><Label>Full name</Label><Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Jane Smith" data-testid="new-trial-name" /></div>
            <div><Label>Password (optional — auto-generated if blank)</Label><Input type="text" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="leave blank for random" data-testid="new-trial-password" /></div>
            <div><Label>Trial days</Label><Input type="number" min={1} max={365} value={newDays} onChange={e => setNewDays(parseInt(e.target.value, 10) || 7)} data-testid="new-trial-days" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={createTrial} disabled={creating || !newEmail || !newName} data-testid="confirm-create-trial">
              {creating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating…</> : 'Create user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONVERT TO PAID DIALOG */}
      <Dialog open={!!convertTarget} onOpenChange={(o) => { if (!o) setConvertTarget(null); }}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-300"><BadgeCheck className="h-5 w-5" /> Manually activate subscription</DialogTitle></DialogHeader>
          <div className="py-2 space-y-2 text-sm">
            <div>Convert <strong>{convertTarget?.email}</strong> to an <Badge className="bg-emerald-900/30 text-emerald-200">active</Badge> subscription?</div>
            <p className="text-slate-300">Use this for offline payments (wire transfer, invoice, contract, etc.). The user will have full access immediately. Their trial_end remains as a reference but is ignored.</p>
            <p className="text-xs text-amber-700"><AlertTriangle className="inline h-3 w-3 mr-1" /> No Stripe charge is created. Logged to audit trail.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertTarget(null)} disabled={converting}>Cancel</Button>
            <Button onClick={convertToPaid} disabled={converting} className="bg-green-700 hover:bg-green-800" data-testid="confirm-convert-btn">
              {converting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Activating…</> : <><ZapOff className="h-4 w-4 mr-1 rotate-180" /> Activate subscription</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter165 — Per-trial engagement drill-down */}
      <UserEngagementDrawer
        userId={drawerUserId}
        open={!!drawerUserId}
        onOpenChange={(v) => !v && setDrawerUserId(null)}
      />
    </div>
  );
};

export default TrialsPage;
